import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  DEFAULT_SHORTCUT_ICON_ID,
  FIXED_SHORTCUT_NAMES,
  SHORTCUT_ICONS,
  SHORTCUT_ICON_IDS,
  SHORTCUT_NAME_MAX_LENGTH,
  validateShortcutName,
} from "./shortcutIcons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SERVICE_SHORTCUT_OPTION = "Special Services";
const CUSTOM_SHORTCUT_OPTION = "__custom__";

interface AddShortcutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (name: string, iconId?: string) => void;
  showServiceShortcut?: boolean;
}

export const AddShortcutDialog: React.FC<AddShortcutDialogProps> = ({
  open,
  onOpenChange,
  onAdd,
  showServiceShortcut = true,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [customName, setCustomName] = useState("");
  const [iconId, setIconId] = useState(DEFAULT_SHORTCUT_ICON_ID);
  const [submitted, setSubmitted] = useState(false);

  const isCustom = name === CUSTOM_SHORTCUT_OPTION;
  const nameError = isCustom ? validateShortcutName(customName) : null;

  const reset = () => {
    setName("");
    setCustomName("");
    setIconId(DEFAULT_SHORTCUT_ICON_ID);
    setSubmitted(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;
    if (isCustom) {
      setSubmitted(true);
      if (nameError) return;
      onAdd(customName.trim(), iconId);
    } else {
      onAdd(name);
    }
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("mobileSettings.dashboard.addShortcutTitle", {
              defaultValue: "Add Shortcut",
            })}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>
              {t("mobileSettings.dashboard.shortcutName", {
                defaultValue: "Name",
              })}
            </Label>
            <Select value={name} onValueChange={setName}>
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={t(
                    "mobileSettings.dashboard.shortcutPlaceholder",
                    {
                      defaultValue: "Select shortcut type",
                    },
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {FIXED_SHORTCUT_NAMES.filter(
                  (option) =>
                    showServiceShortcut || option !== SERVICE_SHORTCUT_OPTION,
                ).map((option) => (
                  <SelectItem key={option} value={option}>
                    {t(`mobileSettings.dashboard.shortcutTypes.${option}`, {
                      defaultValue: option,
                    })}
                  </SelectItem>
                ))}
                <SelectItem value={CUSTOM_SHORTCUT_OPTION}>
                  {t("mobileSettings.dashboard.createCustomShortcut", {
                    defaultValue: "Create Custom Shortcut",
                  })}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isCustom ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="custom-shortcut-name">
                  {t("mobileSettings.dashboard.customShortcutName", {
                    defaultValue: "Shortcut name",
                  })}
                </Label>
                <Input
                  id="custom-shortcut-name"
                  value={customName}
                  maxLength={SHORTCUT_NAME_MAX_LENGTH + 20}
                  onChange={(e) => setCustomName(e.target.value)}
                  aria-invalid={submitted && !!nameError}
                  aria-describedby="custom-shortcut-name-error"
                />
                <p
                  id="custom-shortcut-name-error"
                  role="alert"
                  className="min-h-4 text-xs text-red-600"
                >
                  {submitted && nameError === "required"
                    ? t("mobileSettings.dashboard.customShortcutNameRequired", {
                        defaultValue: "Enter a shortcut name",
                      })
                    : null}
                  {nameError === "tooLong"
                    ? t("mobileSettings.dashboard.customShortcutNameTooLong", {
                        defaultValue: "Name must be at most {{max}} characters",
                        max: SHORTCUT_NAME_MAX_LENGTH,
                      })
                    : null}
                </p>
              </div>
              <div className="space-y-2">
                <Label>
                  {t("mobileSettings.dashboard.customShortcutIcon", {
                    defaultValue: "Icon",
                  })}
                </Label>
                <div
                  role="radiogroup"
                  aria-label={t("mobileSettings.dashboard.customShortcutIcon", {
                    defaultValue: "Icon",
                  })}
                  className="flex flex-wrap gap-2"
                >
                  {SHORTCUT_ICON_IDS.map((id) => {
                    const Icon = SHORTCUT_ICONS[id];
                    return (
                      <button
                        key={id}
                        type="button"
                        role="radio"
                        aria-checked={iconId === id}
                        aria-label={id}
                        onClick={() => setIconId(id)}
                        className={cn(
                          "flex size-9 items-center justify-center rounded-md border focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:outline-none",
                          iconId === id
                            ? "border-zinc-900 bg-zinc-900 text-white"
                            : "border-zinc-200 bg-white text-zinc-600",
                        )}
                      >
                        <Icon className="size-4" />
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" className="bg-zinc-900 text-white">
              {t("mobileSettings.dashboard.addShortcutAction", {
                defaultValue: "Add Shortcut",
              })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
